import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, RefreshCw } from "lucide-react";
import { api, formatDateTime } from "@/lib/api";

const ACTION_COLORS = {
  login_success: "bg-ok/15 text-ok",
  login_failed: "bg-bad/15 text-bad",
  setup_pdg: "bg-citron/15 text-citron",
  user_create: "bg-citron/15 text-citron",
  user_update: "bg-obsidian-800 text-obsidian-300",
  user_delete: "bg-bad/15 text-bad",
  treasury_create: "bg-obsidian-800 text-obsidian-300",
  treasury_delete: "bg-bad/15 text-bad",
  accounting_create: "bg-obsidian-800 text-obsidian-300",
  accounting_delete: "bg-bad/15 text-bad",
  hr_create: "bg-obsidian-800 text-obsidian-300",
  hr_delete: "bg-bad/15 text-bad",
  ai_chat: "bg-citron/10 text-citron",
  change_password: "bg-warn/15 text-warn",
};

export default function Audit() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/audit?limit=500");
      setRows(data);
    } finally { setLoading(false); }
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="audit-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={16} className="text-citron" />
            <h2 className="font-heading text-2xl font-bold tracking-tight">Journal d'audit</h2>
          </div>
          <p className="text-xs text-obsidian-400">Traçabilité immuable · {rows.length} événement(s) récents</p>
        </div>
        <button onClick={load} className="btn-secondary" data-testid="audit-refresh-button">
          <RefreshCw size={14} /> Rafraîchir
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-obsidian-400 border-b border-line">
              <Th>Horodatage</Th><Th>Acteur</Th><Th>Rôle</Th><Th>Action</Th><Th>Cible</Th><Th>IP</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center text-obsidian-500">Chargement…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-12 text-center text-obsidian-500">Aucun événement.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-line/50 hover:bg-obsidian-850">
                <Td><span className="font-mono text-xs">{formatDateTime(r.ts)}</span></Td>
                <Td className="text-obsidian-200 text-xs">{r.user_email}</Td>
                <Td><span className="pill bg-obsidian-800">{r.user_role}</span></Td>
                <Td><span className={`pill font-mono ${ACTION_COLORS[r.action] || "bg-obsidian-800 text-obsidian-300"}`}>{r.action}</span></Td>
                <Td className="font-mono text-xs text-obsidian-400 truncate max-w-xs">{r.target || "—"}</Td>
                <Td className="font-mono text-xs text-obsidian-500">{r.ip || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}

function Th({ children, align = "left" }) { return <th className={`overline px-4 py-3 text-${align}`}>{children}</th>; }
function Td({ children, align = "left", className = "" }) { return <td className={`px-4 py-3 text-${align} ${className}`}>{children}</td>; }
